import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PlatformBrandProjection, type PutPlatformBrandResponseDTO } from '@today/types'
import { useState } from 'react'
import { PLATFORM_BRAND_QUERY_KEY, usePlatformBrandId, writeStoredPlatformBrandId } from '@/brand/use-platform-brand'
import { platformBrandService } from '@/services/platform-brand.service'
import { brandSaveErrorOf } from './useTenantBrand'

/** The warning a save whose parameter-store projection failed shows (TO-541 REQ-108). */
export const PROPAGATION_PENDING_NOTICE = 'Saved. Propagation is pending; save again to retry.'

/**
 * The Super-Admin platform-brand switcher's logic (TO-541 REQ-108): the catalogue,
 * the current pointer, the selection and the save. A saved brand is written into
 * the pointer's query cache (and remembered), so the app re-binds in place with no
 * reload; a `PENDING` projection keeps a warning up until the next selection.
 */
export function usePlatformBrandSwitcher() {
	const client = useQueryClient()
	const currentId = usePlatformBrandId()
	const catalog = useQuery({ queryKey: ['platform-brand-catalog'], queryFn: platformBrandService.listCatalog })
	const [selected, setSelected] = useState<string | null>(null)

	const save = useMutation({
		mutationFn: (brandId: string) => platformBrandService.put(brandId),
		onSuccess: (saved: PutPlatformBrandResponseDTO) => {
			client.setQueryData(PLATFORM_BRAND_QUERY_KEY, { brandId: saved.brandId })
			writeStoredPlatformBrandId(saved.brandId)
		},
	})
	const selectedId = selected ?? currentId

	return {
		options: catalog.data ?? [],
		loading: catalog.isPending,
		currentId,
		selectedId,
		select: (id: string) => {
			save.reset()
			setSelected(id)
		},
		save: () => save.mutate(selectedId),
		saving: save.isPending,
		error: save.isError ? brandSaveErrorOf(save.error) : null,
		notice: save.data?.projection === PlatformBrandProjection.PENDING ? PROPAGATION_PENDING_NOTICE : null,
	}
}
